import { useEffect, useState, FormEvent } from "react";
import { CalendarClock, MessageSquare, PhoneCall, Mail, MapPin, Paperclip, Settings2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { fmtDate, isOverdue, Lead } from "./types";

const toLocalInput = (iso: string | null) => {
  if (!iso) return "";
  const d = new Date(iso);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};

export function FollowUpCard({ lead, onSave }: { lead: Lead; onSave: (b: Record<string, unknown>, msg?: string) => Promise<void> }) {
  const [when, setWhen] = useState(toLocalInput(lead.follow_up_at));
  const [note, setNote] = useState(lead.follow_up_note);
  useEffect(() => { setWhen(toLocalInput(lead.follow_up_at)); setNote(lead.follow_up_note); }, [lead.follow_up_at, lead.follow_up_note]);

  const quick = (days: number) => {
    const d = new Date();
    d.setDate(d.getDate() + days);
    d.setHours(9, 0, 0, 0);
    setWhen(toLocalInput(d.toISOString()));
  };

  return (
    <section className="border border-slate-800 bg-[#161920] p-5" data-testid="follow-up-card">
      <h2 className="mb-3 flex items-center gap-2 font-mono text-xs uppercase text-slate-500"><CalendarClock className="h-4 w-4" /> Follow-up</h2>
      {lead.follow_up_at && (
        <p className={`mb-3 text-sm ${isOverdue(lead) ? "text-red-400" : "text-brand-300"}`} data-testid="follow-up-current">
          {isOverdue(lead) ? "Overdue: " : "Scheduled: "}{fmtDate(lead.follow_up_at)}{lead.follow_up_note && ` — ${lead.follow_up_note}`}
        </p>
      )}
      <div className="mb-2 flex gap-1">
        {[["Tomorrow", 1], ["+3 days", 3], ["+1 week", 7]].map(([l, d]) => (
          <button key={l} onClick={() => quick(d as number)} className="rounded-sm border border-slate-700 px-2 py-1 text-xs text-slate-300 hover:border-brand-500" data-testid={`follow-up-quick-${d}`}>{l}</button>
        ))}
      </div>
      <input type="datetime-local" className="crm-field [color-scheme:dark]" value={when} onChange={(e) => setWhen(e.target.value)} data-testid="follow-up-datetime-input" />
      <input className="crm-field mt-2" placeholder="What's the follow-up? (e.g. send revised quote)" value={note} onChange={(e) => setNote(e.target.value)} data-testid="follow-up-note-input" />
      {lead.gcal?.user_id && (lead.gcal.follow_up || lead.gcal.site_visit) && (
        <p className="mt-2 text-xs text-emerald-400" data-testid="gcal-synced">Synced to owner's Google Calendar</p>
      )}
      <div className="mt-3 flex gap-2">
        <button disabled={!when} onClick={() => onSave({ follow_up_at: new Date(when).toISOString(), follow_up_note: note }, "Follow-up scheduled")}
          className="btn-brand !min-h-0 flex-1 !py-2 !text-base disabled:opacity-40" data-testid="follow-up-save-button">Schedule</button>
        {lead.follow_up_at && (
          <button onClick={() => onSave({ follow_up_at: null, follow_up_note: "" }, "Follow-up cleared")} className="btn-ghost !min-h-0 !py-2 !text-base" data-testid="follow-up-clear-button">Clear</button>
        )}
      </div>
    </section>
  );
}

const KIND_ICON: Record<string, typeof MessageSquare> = { note: MessageSquare, call: PhoneCall, email: Mail, site_visit: MapPin };

export function Attachments({ lead }: { lead: Lead }) {
  if (!lead.attachments?.length) return null;
  const download = async (id: string, name: string) => {
    try {
      const { data } = await api.get(`/files/${id}`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = name;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      toast.error(apiError(e));
    }
  };
  return (
    <div className="mt-4" data-testid="lead-attachments">
      <h3 className="mb-2 font-mono text-[10px] uppercase text-slate-500">Plans & specs</h3>
      <ul className="space-y-1">
        {lead.attachments.map((f) => (
          <li key={f.id}>
            <button onClick={() => download(f.id, f.filename)} data-testid={`lead-attachment-${f.id}`} className="flex w-full items-center gap-2 bg-[#0F1115] px-3 py-2 text-left text-sm text-slate-200 hover:text-brand-400">
              <Paperclip className="h-4 w-4 shrink-0" /><span className="truncate">{f.filename}</span>
              <span className="ml-auto font-mono text-xs text-slate-500">{(f.size / 1024 / 1024).toFixed(1)}MB</span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function NotesCard({ lead, onChange }: { lead: Lead; onChange: (l: Lead) => void }) {
  const [text, setText] = useState("");
  const [kind, setKind] = useState("note");
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!text.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/leads/${lead.id}/notes`, { text, kind });
      onChange(data);
      setText("");
      toast.success("Activity logged");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="border border-slate-800 bg-[#161920] p-5" data-testid="notes-card">
      <h2 className="mb-3 font-mono text-xs uppercase text-slate-500">Notes & activity</h2>
      <form onSubmit={submit} className="space-y-2">
        <textarea className="crm-field min-h-[80px]" placeholder="Add a note, call outcome, site visit summary…" value={text} onChange={(e) => setText(e.target.value)} data-testid="note-text-input" />
        <div className="flex gap-2">
          <select className="crm-field !w-auto" value={kind} onChange={(e) => setKind(e.target.value)} data-testid="note-kind-select">
            <option value="note">Note</option>
            <option value="call">Call</option>
            <option value="email">Email</option>
            <option value="site_visit">Site visit</option>
          </select>
          <button disabled={busy || !text.trim()} className="btn-brand !min-h-0 flex-1 !py-2 !text-base disabled:opacity-40" data-testid="note-submit-button">Log activity</button>
        </div>
      </form>
      <ol className="mt-5 space-y-3 border-l border-slate-700 pl-4" data-testid="activity-log">
        {(lead.activities || []).map((a, i) => {
          const Icon = KIND_ICON[a.type] || Settings2;
          return (
            <li key={i} className="relative" data-testid="activity-item">
              <span className="absolute -left-[25px] top-0.5 grid h-4 w-4 place-items-center rounded-full bg-[#0F1115] ring-1 ring-slate-700"><Icon className="h-2.5 w-2.5 text-brand-500" /></span>
              <p className="whitespace-pre-wrap text-sm text-slate-200">{a.text}</p>
              <p className="font-mono text-[10px] uppercase text-slate-500">{a.type.replace("_", " ")} · {a.by || "system"} · {fmtDate(a.at)}</p>
            </li>
          );
        })}
      </ol>
    </section>
  );
}
